import { useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";

const cooperationEmail =
  "mailto:support@sportarc.cn?subject=%E8%BF%B9%E6%97%B6%E8%BF%90%E5%8A%A8%E6%95%99%E5%B8%88%E7%B3%BB%E7%BB%9F%E5%90%88%E4%BD%9C%E5%92%A8%E8%AF%A2";

const workflowSteps = [
  {
    title: "老师获得课程专属链接",
    description: "每个合作课程都会获得专属链接，老师可以把它直接发给对应学生。",
  },
  {
    title: "学生登录并上传视频",
    description: "学生从老师发来的链接进入，完成手机号登录后上传训练视频。",
  },
  {
    title: "AI 生成动作分析",
    description: "系统处理视频并生成动作数据、3D 视图和针对性的训练建议。",
  },
  {
    title: "老师集中查看结果",
    description: "老师在看板中按学生查看分析记录，让下一次指导更有依据。",
  },
];

const socialProfiles = [
  {
    name: "抖音",
    href: "https://v.douyin.com/69ogZhNzs5U/",
    icon: "/social/douyin.svg",
  },
  {
    name: "小红书",
    href: "https://www.xiaohongshu.com/user/profile/67775b52000000001801d365",
    icon: "/social/xiaohongshu.svg",
  },
  {
    name: "哔哩哔哩",
    href: "https://space.bilibili.com/3546973124692674",
    icon: "/social/bilibili.svg",
  },
];

function scrollToHomeSection(event: MouseEvent<HTMLAnchorElement>, sectionId: string) {
  event.preventDefault();
  const section = document.getElementById(sectionId);

  if (!section) {
    return;
  }

  section.focus({ preventScroll: true });
  section.scrollIntoView({
    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    block: "start",
  });
}

export function HomePage() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  return (
    <div className="teacher-home">
      <a
        className="teacher-home-skip"
        href="#/"
        onClick={(event) => scrollToHomeSection(event, "teacher-home-main")}
      >
        跳到主要内容
      </a>

      <header className="teacher-home-header">
        <div className="teacher-home-container teacher-home-nav">
          <Link
            className="teacher-home-brand"
            to="/"
            aria-label="迹时运动教师端首页"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <img src="/pingpi-logo.png?v=cff2de3ac9d0" alt="" />
            <span>
              <strong>迹时运动</strong>
            </span>
          </Link>

          <nav
            className={`teacher-home-nav-links${isMobileMenuOpen ? " is-open" : ""}`}
            id="teacher-home-navigation"
            aria-label="主要导航"
          >
            <a
              className="teacher-home-nav-text"
              href="#/"
              onClick={(event) => {
                setIsMobileMenuOpen(false);
                scrollToHomeSection(event, "student-guide");
              }}
            >
              学生如何使用
            </a>
            <Link
              className="teacher-home-nav-text"
              to="/teacher/dashboard"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              老师登录
            </Link>
            <a
              className="teacher-home-nav-action"
              href={cooperationEmail}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              申请合作
            </a>
          </nav>
          <button
            className="teacher-home-mobile-menu-toggle"
            type="button"
            aria-label="菜单"
            aria-controls="teacher-home-navigation"
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>
        </div>
      </header>

      <main id="teacher-home-main" tabIndex={-1}>
        <section className="teacher-home-hero">
          <div className="teacher-home-container teacher-home-hero-layout">
            <div className="teacher-home-hero-copy">
              <h1>
                让每一次训练，
                <br />
                都有清晰反馈。
              </h1>
              <p>
                学生通过老师的专属链接上传乒乓球训练视频，迹时运动 AI
                生成动作数据、3D 视图和训练建议，老师在看板中集中查看每位学生的分析记录。
              </p>
              <div className="teacher-home-hero-actions">
                <a className="teacher-home-button teacher-home-button-primary" href={cooperationEmail}>
                  申请合作
                </a>
                <Link
                  className="teacher-home-button teacher-home-button-secondary"
                  to="/teacher/dashboard"
                >
                  老师登录
                </Link>
              </div>
              <span className="teacher-home-hero-note">
                面向学校、俱乐部与老师团队。
              </span>
            </div>

            <figure className="teacher-home-product-visual">
              <img
                src="/teacher-home-ai-coach.png"
                alt="迹时运动 AI教练的 3D 动作分析界面"
              />
              <figcaption>App 内部真实 AI教练分析界面</figcaption>
            </figure>
          </div>
        </section>

        <section className="teacher-home-gallery" aria-labelledby="teacher-home-gallery-title">
          <div className="teacher-home-container teacher-home-gallery-heading">
            <h2 id="teacher-home-gallery-title">把每个细节都变成训练反馈。</h2>
            <p>
              在乒乓球训练中，以 3D
              查看动作、测量运动数据，并把关键时刻变成下一次训练可用的反馈。
            </p>
          </div>
          <div
            className="teacher-home-container teacher-home-gallery-row"
            role="group"
            aria-label="迹时运动 AI教练动作分析界面"
            tabIndex={0}
          >
            <img
              src="/teacher-home-gallery-9.png"
              alt="迹时运动 AI教练动作测量数据"
              width="1242"
              height="2688"
              loading="lazy"
              decoding="async"
            />
            <img
              src="/teacher-home-gallery-8.png"
              alt="迹时运动 AI教练动作评分"
              width="1242"
              height="2688"
              loading="lazy"
              decoding="async"
            />
            <img
              src="/teacher-home-gallery-10.png"
              alt="迹时运动 AI教练训练反馈"
              width="1242"
              height="2688"
              loading="lazy"
              decoding="async"
            />
          </div>
        </section>

        <section className="teacher-home-workflow" aria-labelledby="workflow-title">
          <div className="teacher-home-container teacher-home-section-layout">
            <div className="teacher-home-section-heading">
              <h2 id="workflow-title">从训练视频到教学反馈</h2>
              <p>
                一个专属链接连接老师、学生和每次训练结果，不需要学生自行寻找入口。
              </p>
            </div>

            <ol className="teacher-home-step-list">
              {workflowSteps.map((step, index) => (
                <li key={step.title}>
                  <span className="teacher-home-step-number" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section
          className="teacher-home-student"
          id="student-guide"
          aria-labelledby="student-guide-title"
          tabIndex={-1}
        >
          <div className="teacher-home-container teacher-home-student-layout">
            <div>
              <span className="teacher-home-section-index">学生入口</span>
              <h2 id="student-guide-title">学生从老师发来的链接开始</h2>
            </div>
            <div className="teacher-home-student-copy">
              <p>
                请直接打开老师发送的专属课程链接，页面会显示对应的老师与课程信息，并引导你完成登录和视频上传。
              </p>
              <p>
                如果还没有收到链接，或链接已经失效，请联系老师重新获取。这里不提供通用学生登录入口。
              </p>
            </div>
          </div>
        </section>

        <section className="teacher-home-cooperation" aria-labelledby="cooperation-title">
          <div className="teacher-home-container teacher-home-cooperation-layout">
            <h2 id="cooperation-title">想让老师和学生先用起来？</h2>
            <p>
              告诉我们学校、俱乐部或课程的基本情况，我们会结合实际教学安排制定合作周期和
              AI教练额度。
            </p>
          </div>
        </section>
      </main>

      <footer className="teacher-home-footer">
        <div className="teacher-home-container teacher-home-footer-layout">
          <div className="teacher-home-footer-brand-column">
            <div className="teacher-home-footer-brand">
              <img src="/pingpi-logo.png?v=cff2de3ac9d0" alt="" />
              <span>迹时运动</span>
            </div>
            <p>让训练视频成为可以持续跟进的教学反馈。</p>
          </div>

          <nav className="teacher-home-footer-column" aria-label="了解更多">
            <h3>了解更多</h3>
            <div className="teacher-home-footer-links">
              <a href="https://www.sportarc.cn" target="_blank" rel="noreferrer">
                迹时运动官网
              </a>
              <Link to="/teacher/dashboard">老师登录</Link>
              <a
                href="#/"
                onClick={(event) => scrollToHomeSection(event, "student-guide")}
              >
                学生如何使用
              </a>
            </div>
          </nav>

          <nav className="teacher-home-footer-column" aria-label="帮助与法律">
            <h3>帮助与法律</h3>
            <div className="teacher-home-footer-links">
              <a href="https://www.sportarc.cn/support/" target="_blank" rel="noreferrer">
                使用支持
              </a>
              <a href="https://www.sportarc.cn/terms/" target="_blank" rel="noreferrer">
                使用条款
              </a>
              <a href="https://www.sportarc.cn/privacy/" target="_blank" rel="noreferrer">
                隐私政策
              </a>
            </div>
          </nav>

          <div className="teacher-home-contact teacher-home-footer-contact">
            <h3>联系我们</h3>
            <a className="teacher-home-email" href={cooperationEmail}>
              support@sportarc.cn
            </a>
            <div className="teacher-home-social-links">
              {socialProfiles.map((profile) => (
                <a
                  key={profile.name}
                  className="teacher-home-social-link"
                  href={profile.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`在${profile.name}关注迹时运动`}
                  title={profile.name}
                >
                  <img src={profile.icon} alt="" aria-hidden="true" />
                </a>
              ))}
              <a
                className="teacher-home-social-link"
                href={cooperationEmail}
                aria-label="发送合作邮件"
                title="邮件"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M3 5h18v14H3z" />
                  <path d="m3 6 9 7 9-7" />
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div className="teacher-home-container teacher-home-footer-bottom">
          <p>Copyright © 2026 迹时运动。保留所有权利。</p>
        </div>
      </footer>
    </div>
  );
}
